import { ColumnDef } from "@tanstack/react-table";
import {
  FILTER_DELIMITER,
  FormTabsTypes,
  SearchAndFilterTypes,
} from "src/constants";
import { OrganizationError } from "src/hooks/useOrganization";
import { WithId } from "src/types/formTemplate";
import { GlossaryFn } from "src/types/glossary";
import type * as GQL from "src/types/graphql";
import { RemoteData } from "src/types/remoteData";
import { TagGroup } from "src/types/tags";
import { FormListRow } from "../types";
import { buildAttendanceColumnDef } from "./attendanceColumns";
import {
  ALL_FORM_LIST_COLUMNS,
  ColumnId,
  defaultColumnsByTabType,
} from "./constants";
import { buildEligibilityOverrideColumnDef } from "./eligibilityOverrideColumn";
import { buildErrorsColumnDef } from "./errorsColumn";
import {
  buildFormSchoolRowIdColumnDef,
  buildIdColumnDef,
} from "./formIdColumn";
import { buildGradeColumnDef } from "./gradeColumn";
import { buildGuardianColumnDef } from "./guardianColumns";
import { buildLotteryNumberColumnDef } from "./lotteryNumberColumn";
import { buildLotteryPositionColumnDef } from "./lotteryPositionColumn";
import { buildOverrideReasonColumnDef } from "./overrideReasonColumn";
import { buildProgramColumnDef } from "./programColumns";
import { buildQuestionAnswerColumns } from "./questionAnswerColumns";
import { buildSchoolColumnDef } from "./schoolColumn";
import {
  buildSchoolIdColumnDef,
  buildSchoolReferenceIdColumnDef,
} from "./schoolIdColumn";
import { buildSchoolRankColumnDef } from "./schoolRankColumn";
import { buildSchoolSubmittedDateColumnDef } from "./schoolSubmittedDateColumn";
import {
  buildStatusColumnDef,
  buildStatusDateColumnDef,
} from "./statusColumns";
import {
  buildStudentAddressColumnDef,
  buildStudentColumnDef,
  buildStudentDobColumnDef,
} from "./studentColumns";
import { buildSubmittedDateColumnDef } from "./submittedDateColumn";
import {
  buildSubStatusColumnDef,
  buildSubStatusDateColumnDef,
} from "./subStatusColumns";
import { buildFormTagsColumnDef, buildTagsColumnDef } from "./tagsColumn";
import { buildTiebreakerColumnDef } from "./tiebreakerColumn";
import { buildVerificationsColumnDef } from "./verificationsColumn";
import { buildVisibilityColumnDef } from "./visibilityColumn";
import { buildWaitlistPositionColumnDef } from "./waitlistPositionColumn";

export * from "./visibilityColumn";

const buildColumnDefsMap = (
  columnIds: ColumnId[],
  buildColumnDefFn: (columnId: ColumnId) => ColumnDef<FormListRow> | null
) => {
  return columnIds.reduce<Record<ColumnId, ColumnDef<FormListRow> | null>>(
    (acc, columnId) => ({
      ...acc,
      [columnId]: buildColumnDefFn(columnId),
    }),
    {} as Record<ColumnId, ColumnDef<FormListRow> | null>
  );
};

export const buildColumnsForTab = ({
  tabType,
  additionalColumnsIncluded,
  glossary,
  organization,
  formTemplate,
  rankingEnabled,
  isSchoolAdmin,
  hasOfferReadPermission,
  tagGroups,
  hasVerifications,
  isCurrentApplyingSchoolsEnabled,
}: {
  tabType: FormTabsTypes;
  additionalColumnsIncluded: boolean;
  glossary: GlossaryFn;
  organization: RemoteData<OrganizationError, GQL.Organization>;
  formTemplate: GQL.FormTemplateFragment | null;
  rankingEnabled: boolean;
  isSchoolAdmin: boolean;
  hasOfferReadPermission: boolean;
  tagGroups: TagGroup<WithId>[];
  hasVerifications: boolean;
  isCurrentApplyingSchoolsEnabled: boolean;
}): ColumnDef<FormListRow>[] => {
  const columnIdDef = buildIdColumnDef<FormListRow>();
  const formSchoolRowIdColumnDef = buildFormSchoolRowIdColumnDef<FormListRow>();
  const studentColumnDefs = buildColumnDefsMap(
    [
      ColumnId.StudentFirstName,
      ColumnId.StudentMiddleName,
      ColumnId.StudentLastName,
      ColumnId.StudentId,
    ],
    (columnId) =>
      buildStudentColumnDef<FormListRow>({
        glossary,
        organization,
        formTemplateId: formTemplate?.id ?? "",
        columnId,
      })
  );

  const rankColumnDef =
    rankingEnabled && !isSchoolAdmin
      ? buildSchoolRankColumnDef<FormListRow>()
      : null;
  const eligibilityOverrideColumnDef =
    buildEligibilityOverrideColumnDef<FormListRow>();
  const overrideReasonColumnDef = buildOverrideReasonColumnDef<FormListRow>();
  const schoolColumnDef = buildSchoolColumnDef<FormListRow>({ glossary });
  const schoolIdColumnDef = buildSchoolIdColumnDef<FormListRow>({ glossary });
  const schoolReferenceIdColumnDef =
    buildSchoolReferenceIdColumnDef<FormListRow>({ glossary });
  const gradeColumnDef = buildGradeColumnDef<FormListRow>();
  const statusColumnDef = buildStatusColumnDef<FormListRow>({});
  const statusDateColumnDef = buildStatusDateColumnDef<FormListRow>({
    organization,
  });
  const subStatusColumnDef = hasOfferReadPermission
    ? buildSubStatusColumnDef<FormListRow>({ organization })
    : null;
  const subStatusDateColumnDef = hasOfferReadPermission
    ? buildSubStatusDateColumnDef<FormListRow>({
        organization,
      })
    : null;
  const formTagsColumnDef = buildFormTagsColumnDef<FormListRow>();
  const tagsColumnDef = tagGroups?.map((tagGroup) =>
    buildTagsColumnDef<FormListRow>(tagGroup)
  );
  const verificationsColumnDef =
    hasVerifications && formTemplate
      ? buildVerificationsColumnDef<FormListRow>({ formTemplate, glossary })
      : null;
  const errorsColumnDef = buildErrorsColumnDef<FormListRow>();
  const waitlistPositionColumnDef =
    buildWaitlistPositionColumnDef<FormListRow>();
  const tiebreakerColumnDef = buildTiebreakerColumnDef<FormListRow>();
  const visibilityColumnDef = buildVisibilityColumnDef<FormListRow>();
  const lotteryNumberColumnDef = buildLotteryNumberColumnDef<FormListRow>();
  const lotteryPositionColumnDef = buildLotteryPositionColumnDef<FormListRow>();
  const attendanceColumnDefs = isCurrentApplyingSchoolsEnabled
    ? buildColumnDefsMap(
        [
          ColumnId.AttendanceStatus,
          ColumnId.AttendingSchool,
          ColumnId.AttendingSchoolId,
          ColumnId.AttendingSchoolReferenceId,
        ],
        (columnId) => buildAttendanceColumnDef<FormListRow>(columnId)
      )
    : null;
  const programColumnDefs = buildColumnDefsMap(
    [ColumnId.Program, ColumnId.ProgramId],
    (columnId) => buildProgramColumnDef<FormListRow>(columnId)
  );
  const submittedDateColumnDef = buildSubmittedDateColumnDef<FormListRow>({
    organization,
  });
  const schoolSubmittedDateColumnDef =
    buildSchoolSubmittedDateColumnDef<FormListRow>({
      organization,
    });
  const studentAddressColumnDef =
    buildStudentAddressColumnDef<FormListRow>(glossary);
  const studentDobColumnDef = buildStudentDobColumnDef<FormListRow>(glossary);

  const guardianColumnDefs = additionalColumnsIncluded
    ? buildColumnDefsMap(
        [
          ColumnId.GuardianFirstName,
          ColumnId.GuardianMiddleName,
          ColumnId.GuardianLastName,
          ColumnId.GuardianId,
          ColumnId.GuardianAddress,
          ColumnId.GuardianEmail,
          ColumnId.GuardianPhone,
          ColumnId.GuardianLanguage,
        ],
        (columnId) => buildGuardianColumnDef<FormListRow>(columnId)
      )
    : null;

  const questionAnswerColumnDefs = additionalColumnsIncluded
    ? buildQuestionAnswerColumns<FormListRow>({
        formTemplateGQL: formTemplate,
        organization,
      })
    : null;

  const columnIdToColumnDef: Record<
    ColumnId,
    ColumnDef<FormListRow> | ColumnDef<FormListRow>[] | null
  > = {
    ...studentColumnDefs,
    ...guardianColumnDefs,
    ...attendanceColumnDefs,
    ...programColumnDefs,
    [ColumnId.ID]: columnIdDef,
    [ColumnId.FormSchoolRowId]: formSchoolRowIdColumnDef,
    [ColumnId.SchoolRank]: rankColumnDef,
    [ColumnId.EligibilityOverride]: eligibilityOverrideColumnDef,
    [ColumnId.OverrideReason]: overrideReasonColumnDef,
    [ColumnId.School]: schoolColumnDef,
    [ColumnId.SchoolId]: schoolIdColumnDef,
    [ColumnId.SchoolReferenceId]: schoolReferenceIdColumnDef,
    [ColumnId.Grade]: gradeColumnDef,
    [ColumnId.Status]: statusColumnDef,
    [ColumnId.StatusDate]: statusDateColumnDef,
    [ColumnId.Errors]: errorsColumnDef,
    [ColumnId.SubStatus]: subStatusColumnDef,
    [ColumnId.SubStatusDate]: subStatusDateColumnDef,
    [ColumnId.Tags]: [...tagsColumnDef, formTagsColumnDef],
    [ColumnId.Verifications]: verificationsColumnDef,
    [ColumnId.WaitlistPosition]: waitlistPositionColumnDef,
    [ColumnId.Tiebreaker]: tiebreakerColumnDef,
    [ColumnId.Visibility]: visibilityColumnDef,
    [ColumnId.LotteryNumber]: lotteryNumberColumnDef,
    [ColumnId.LotteryPosition]: lotteryPositionColumnDef,
    [ColumnId.SchoolSubmittedDate]: schoolSubmittedDateColumnDef,
    [ColumnId.StudentAddress]: studentAddressColumnDef,
    [ColumnId.StudentDateOfBirth]: studentDobColumnDef,
    [ColumnId.SubmittedDate]: submittedDateColumnDef,
    [ColumnId.Question]: questionAnswerColumnDefs,
  };

  const buildColumnDefsList = (columnIds: ColumnId[]) => {
    const columns: ColumnDef<FormListRow>[] = [];
    columnIds.forEach((columnId) => {
      const columnDef = columnIdToColumnDef[columnId];
      if (Array.isArray(columnDef)) {
        columns.push(...columnDef.filter(Boolean));
      } else if (columnDef !== null && columnDef !== undefined) {
        columns.push(columnDef);
      }
    });
    return columns;
  };

  // We only want to include the additional columns in the table if any non-default columns are selected.
  // This supports the following behavior:
  // - When only default columns are selected, the `View columns` selector will show the default columns list.
  // - When additional columns are selected, the `View columns` selector will have a button to go to
  //   a page of the longer list of columns.
  if (additionalColumnsIncluded) {
    return buildColumnDefsList(ALL_FORM_LIST_COLUMNS);
  } else {
    return buildColumnDefsList(defaultColumnsByTabType[tabType]);
  }
};

export const hasAdditionalColumns = (
  searchParams: URLSearchParams,
  defaultColumnsProps: DefaultColumnsProps
) => {
  const visibleColumns = searchParams.get(SearchAndFilterTypes.VisibleColumns);
  if (!visibleColumns) {
    return false;
  }
  const tabType =
    (searchParams.get(SearchAndFilterTypes.Tab) as FormTabsTypes) ??
    FormTabsTypes.All;
  const defaultColumnIds = getDefaultColumnIdsForTab({
    tabType,
    ...defaultColumnsProps,
  });
  const visibleColumnIds = visibleColumns.split(FILTER_DELIMITER);
  return visibleColumnIds.some(
    (columnId) => !defaultColumnIds.includes(columnId)
  );
};

export type DefaultColumnsProps = {
  isSchoolAdmin: boolean;
  hasOfferReadPermission: boolean;
  tagGroups: TagGroup<WithId>[];
  rankingEnabled: boolean;
  hasVerifications: boolean;
};

export const getDefaultColumnIdsForTab = ({
  tabType,
  tagGroups,
  rankingEnabled,
  isSchoolAdmin,
  hasOfferReadPermission,
  hasVerifications,
}: {
  tabType: FormTabsTypes;
} & DefaultColumnsProps) => {
  const defaultColumnIds: string[] = [];
  const tagGroupColumnIds = tagGroups.map((tagGroup) =>
    getDynamicColumnId("tags", tagGroup.id)
  );
  const skipSchoolRank = !rankingEnabled || isSchoolAdmin;
  const skipSubStatus = !hasOfferReadPermission;
  defaultColumnsByTabType[tabType].forEach((columnId) => {
    if (
      (columnId === ColumnId.SchoolRank && skipSchoolRank) ||
      (columnId === ColumnId.SubStatus && skipSubStatus) ||
      (columnId === ColumnId.SubStatusDate && skipSubStatus) ||
      (columnId === ColumnId.Verifications && !hasVerifications)
    ) {
      return;
    }
    if (columnId === ColumnId.Tags) {
      defaultColumnIds.push(...tagGroupColumnIds);
    }
    defaultColumnIds.push(columnId);
  });
  return defaultColumnIds;
};

export const getAllColumnIds = (
  columnIds: ColumnId[],
  tagGroups: TagGroup<WithId>[],
  questionIdsAndLabelsInOrder?: { id: uuid; label: string }[],
  disclaimerIdsAndLabels?: { id: uuid; label: string }[]
) => {
  const allColumnIds: string[] = [];
  const tagGroupColumnIds = tagGroups.map((tagGroup) =>
    getDynamicColumnId("tags", tagGroup.id)
  );
  columnIds.forEach((columnId) => {
    if (columnId === ColumnId.Tags) {
      allColumnIds.push(...tagGroupColumnIds);
      allColumnIds.push(columnId);
    } else if (columnId === ColumnId.Question) {
      if (questionIdsAndLabelsInOrder) {
        allColumnIds.push(
          ...questionIdsAndLabelsInOrder.map((question) =>
            getDynamicColumnId("question", question.id)
          )
        );
      }
      if (disclaimerIdsAndLabels) {
        allColumnIds.push(
          ...disclaimerIdsAndLabels.map((disclaimer) =>
            getDynamicColumnId("disclaimer", disclaimer.id)
          )
        );
      }
    } else {
      allColumnIds.push(columnId);
    }
  });
  return allColumnIds;
};

export function getDynamicColumnId(prefix: string, id: string): string {
  return `${prefix}-${id}`;
}
